import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { API_URL } from '../../../apiConfig';
import sanitizeHtml from 'sanitize-html';
import debounce from 'lodash.debounce';
import star from "../../../image/shines.png";

const AssessmentPage = () => {
  const [error, setError] = useState(null);
  const [adminData, setAdminData] = useState(null);
  const [assessments, setAssessments] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const assessmentsPerPage = 6;
  const navigate = useNavigate();

  // Load adminData from sessionStorage on mount
  useEffect(() => {
    const storedAdminData = JSON.parse(sessionStorage.getItem('adminData'));
    console.log('Loaded adminData:', storedAdminData);
    setAdminData(storedAdminData);
  }, []);

  // Fetch assessments on mount and when searchQuery changes
  useEffect(() => {
    const fetchAssessments = async () => {
      try {
        const url = `${API_URL}/api/v1/admin/assessments${searchQuery ? `?search=${encodeURIComponent(searchQuery)}` : ''}`;
        console.log('Fetching from URL:', url);
        const response = await fetch(url, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          },
          credentials: 'include',
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.message || 'Failed to fetch assessments');
        }

        const result = await response.json();
        console.log('API Response:', result);
        setAssessments(result.assessments || []);
      } catch (err) {
        setError(err.message);
        console.error('Error fetching assessments:', err);
      }
    };

    fetchAssessments();
  }, [searchQuery]);

  // Helper function to get status label and badge color
  const getStatusInfo = (status) => {
    switch (status) {
      case 0:
        return { label: 'Draft', color: '#F97316' }; // Orange
      case 1:
        return { label: 'Published', color: '#22C55E' }; // Green
      case 2:
        return { label: 'Unpublished', color: '#6B7280' }; // Gray
      default:
        return { label: 'Unknown', color: '#6B7280' };
    }
  };

  // Helper function to sanitize and render HTML
  const renderHTML = (html) => {
    const sanitizedHTML = sanitizeHtml(html, {
      allowedTags: ['p', 'b', 'i', 'u', 's', 'a', 'ul', 'ol', 'li', 'h1', 'h2', 'h3', 'blockquote', 'table', 'tr', 'td', 'th', 'strong', 'em'],
      allowedAttributes: {
        a: ['href'],
        table: ['border'],
        td: ['colspan', 'rowspan'],
        th: ['colspan', 'rowspan'],
      },
    });
    return { __html: sanitizedHTML };
  };

  // Debounced search handler
  const debouncedSetSearchQuery = useCallback(
    debounce((value) => {
      console.log('Search query updated:', value);
      setSearchQuery(value);
      setCurrentPage(1);
    }, 500),
    []
  );

  const handleSearchChange = (e) => {
    debouncedSetSearchQuery(e.target.value);
  };

  // Filter assessments by title (client-side fallback)
  const filteredAssessments = assessments.filter((assessment) =>
    assessment.title.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Calculate paginated assessments
  const indexOfLastAssessment = currentPage * assessmentsPerPage;
  const indexOfFirstAssessment = indexOfLastAssessment - assessmentsPerPage;
  const currentAssessments = filteredAssessments.slice(indexOfFirstAssessment, indexOfLastAssessment);
  const totalPages = Math.ceil(filteredAssessments.length / assessmentsPerPage);

  // Handle page change
  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-gray-100 p-6 md:p-8">
      {/* Header Section */}
      <h1 className="text-3xl font-bold text-gray-800 mb-6">Assessments</h1>
      <div className="mx-auto mb-8 flex justify-between items-center">
        {/* Search Box */}
        <div className="col-md-4">
          <input
            type="text"
            value={searchQuery}
            onChange={handleSearchChange}
            placeholder="Search assessments by title..."
            className="w-full max-w-lg p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
        <button
          onClick={() => navigate('/admin/Ai-assessment')}
          className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors duration-200 font-medium"
        >
          AI Assessment
        </button>
        <button
          onClick={() => navigate('/admin/add-assessment')}
          className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors duration-200 font-medium"
        >
          Add Assessment
        </button>
      </div>

      {/* Assessments Grid */}
      <div className="mx-auto">
        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg text-red-600 text-sm col-span-full">
            {error}
          </div>
        )}
        {filteredAssessments.length === 0 ? (
          <div className="text-gray-600 text-center py-8">
            {searchQuery
              ? `No assessments found matching "${searchQuery}".`
              : 'No assessments available. Add a new assessment to get started.'}
          </div>
        ) : (
          <>
            <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
              {currentAssessments.map((assessment) => {
                const { label, color } = getStatusInfo(assessment.status);
                return (
                  <div
                    key={assessment._id}
                    className="bg-white p-6 rounded-2xl shadow-lg hover:shadow-xl transition-shadow duration-200 relative border border-gray-200"
                  >
                    {/* Status Badge */}
                    <div
                      className="absolute right-4 top-0 transform -translate-y-1/2"
                      style={{
                        backgroundColor: color,
                        color: 'white',
                        fontSize: '12px',
                        padding: '4px 12px',
                        borderRadius: '12px',
                        fontWeight: '500',
                      }}
                    >
                      {label}
                    </div>
                    {/* Title */}
                    <h2 className="text-xl font-semibold text-gray-800 mb-3 hover:text-blue-600 transition-colors duration-200">
                      <Link to={`/admin/assessments/${assessment._id}`}>
                        {assessment.title}
                      </Link>
                    </h2>
                    {/* Description (Rendered HTML) */}
                    <div
                      className="text-gray-600 mb-4 line-clamp-3 text-sm"
                      dangerouslySetInnerHTML={renderHTML(assessment.description)}
                    />
                    {/* Metadata */}
                    <div className="text-sm text-gray-500 space-y-1 mb-4">
                      <p>
                        <strong>ID:</strong> {assessment._id}
                      </p>
                      <p>
                        <strong>Duration:</strong> {Math.floor(assessment.duration / 60)}h{' '}
                        {assessment.duration % 60}m
                      </p>
                      <p>
                        <strong>Creator:</strong> {assessment.creator?.name || 'Unknown'}
                      </p>
                    </div>
                    <div className='flex' style={{justifyContent:"space-between"}}>
                        <div>
                          <button
                            type="button"
                            style={{
                              fontWeight: 600,
                              backgroundColor: '#e0f7fa',
                              color: '#00695c',
                              padding: '6px 12px',
                              border: 'none',
                              borderRadius: '10px',
                              fontSize: '16px',
                              cursor: 'pointer',
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: '4px',
                              transition: 'background-color 0.2s',
                            }}
                            onClick={() => navigate(`/admin/assessments/${assessment._id}/results`)}
                            onMouseOver={(e) => (e.currentTarget.style.backgroundColor = '#b2ebf2')}
                            onMouseOut={(e) => (e.currentTarget.style.backgroundColor = '#e0f7fa')}
                          >
                            Attempt- <span style={{ fontWeight: 600, color: '#2e7d32' }}>{assessment.userCount || 0}</span>
                          </button>
                        </div>
                         <div style={{ display: assessment.isAiGenerated === 1 ? 'block' : 'none' }}>
                              <button
                                type="button"
                                className='highlight_bg'
                                style={{
                                  fontWeight: 600,
                                  backgroundColor: '#e8f5e9', // Light green background for contrast
                                  color: '#2e7d32', // Green text to match Attempt button's span
                                  padding: '6px 12px',
                                  border: 'none',
                                  borderRadius: '10px',
                                  fontSize: '16px',
                                  cursor: 'pointer',
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  gap: '4px',
                                  transition: 'background-color 0.2s',
                                }}
                                onClick={() => console.log('AI Gen button clicked')} // Placeholder action
                                onMouseOver={(e) => (e.currentTarget.style.backgroundColor = '#c8e6c9')}
                                onMouseOut={(e) => (e.currentTarget.style.backgroundColor = '#e8f5e9')}
                              >
                                <img src={star} className="w-4 mr-1" alt="AI" />
                              </button>
                            </div>
                    </div>
                  </div>
                );
              })}
            </div>
            {/* Pagination */}
            {totalPages > 1 && (
              <div className="mt-8 flex justify-center space-x-2">
                {Array.from({ length: totalPages }, (_, index) => index + 1).map((page) => (
                  <button
                    key={page}
                    onClick={() => handlePageChange(page)}
                    className={`px-4 py-2 rounded-lg ${
                      currentPage === page
                        ? 'bg-blue-600 text-white'
                        : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                    } transition-colors duration-200`}
                  >
                    {page}
                  </button>
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default AssessmentPage;